import {
	Box,
	type SxProps,
	Table,
	TableCell,
	TableContainer,
	TableRow,
	type Theme,
	Typography,
} from '@mui/material';
import React from 'react';
import type { Components } from 'react-markdown';

import { borderRadius, palette, spaces } from '../../assets/styles/themes/tokens';

const textColor = {
	color: palette.text.primary,
};

/**
 * Default markdown components configuration for ReactMarkdown.
 * Maps markdown elements to MUI components with consistent styling.
 */
export const getMarkdownComponents = (
	imageWidthsByIndex?: Map<number, string>,
	onImageClick?: (src: string, alt?: string) => void
): Components => {
	// Track image render index for width mapping
	let imageRenderIndex = 0;

	return {
		h1: (props) => <Typography variant="h5" component="h1" gutterBottom sx={textColor} {...props} />,
		h2: (props) => <Typography variant="h6" component="h2" gutterBottom sx={textColor} {...props} />,
		h3: (props) => <Typography variant="h6" component="h3" gutterBottom sx={textColor} {...props} />,
		h4: (props) => <Typography variant="h6" component="h4" gutterBottom sx={textColor} {...props} />,
		h5: (props) => <Typography variant="h6" component="h5" gutterBottom sx={textColor} {...props} />,
		h6: (props) => <Typography variant="h6" component="h6" gutterBottom sx={textColor} {...props} />,
		p: (props) => <Typography variant="body1" paragraph sx={textColor} {...props} />,
		li: (props) => (
			<li>
				<Typography variant="body1" sx={textColor} {...props} />
			</li>
		),
		table: (props) => (
			<TableContainer sx={{ marginBottom: spaces.md }}>
				<Table {...props} />
			</TableContainer>
		),
		td: (props) => <TableCell {...props} align="left" />,
		th: (props) => <TableCell {...props} align="center" />,
		tr: (props) => <TableRow {...props} />,
		img: ({ style, ...props }) => {
			const clickable = Boolean(onImageClick && props.src);
			const handleClick = clickable
				? () => onImageClick!(props.src as string, props.alt as string | undefined)
				: undefined;

			if (imageWidthsByIndex) {
				const width = imageWidthsByIndex.get(imageRenderIndex);
				imageRenderIndex += 1;
				const mergedStyle = width
					? { ...(style || {}), width, maxWidth: '100%', height: 'auto' }
					: style;
				return (
					<Box
						component="img"
						{...props}
						onClick={handleClick}
						sx={{
							...(mergedStyle as SxProps<Theme>),
							...(clickable ? { cursor: 'pointer' } : {}),
						}}
					/>
				);
			}
			return (
				<Box
					component="img"
					{...props}
					onClick={handleClick}
					sx={{
						maxWidth: 'min(100%, 460px)',
						height: 'auto',
						borderRadius: borderRadius.md,
						...(clickable ? { cursor: 'pointer' } : {}),
						...style,
					}}
				/>
			);
		},
	};
};
